export default {
  data() {
    return {
      rutValidationState: {
        isValid: false,
        isComplete: false,
        message: '',
        showError: false
      }
    }
  },
  
  methods: {
    /**
     * Valida un RUT chileno en tiempo real
     * @param {string} rut - El RUT a validar (formato: 00.000.000-K)
     * @returns {Object} - Estado de validación
     */
    validateRutChile(rut) {
      // Limpiar el RUT
      const cleanRut = rut ? rut.toString().toLowerCase().trim() : '';
      
      // Si está vacío o tiene muy pocos caracteres, mostrar mensaje de requerido
      if (!cleanRut || cleanRut.length < 3) {
        return {
          isValid: false,
          isComplete: false,
          message: 'El RUT es requerido',
          showError: true
        };
      }
      
      // Verificar formato básico con regex - más permisivo mientras se escribe
      const basicFormat = /^[0-9]{1,2}\.[0-9]{1,3}\.[0-9]{1,3}-[0-9k]$/;
      
      if (!basicFormat.test(cleanRut)) {
        return {
          isValid: false,
          isComplete: cleanRut.length >= 10,
          message: 'Formato inválido. Use: 00.000.000-K',
          showError: cleanRut.length >= 3 // Mostrar error de formato desde 3 caracteres
        };
      }
      
      // Extraer número y dígito verificador
      const parts = cleanRut.split('-');
      const rutNumber = parts[0].replace(/\./g, '');
      const dv = parts[1];
      
      // Verificar que el RUT tenga al menos 7 dígitos
      if (rutNumber.length < 7) {
        return {
          isValid: false,
          isComplete: false,
          message: 'RUT incompleto',
          showError: cleanRut.length >= 3 // Mostrar error de incompleto desde 3 caracteres
        };
      }
      
      // Calcular dígito verificador
      const calculatedDv = this.calculateDv(rutNumber);
      
      // Comparar dígito verificador
      if (calculatedDv.toString() === dv) {
        return {
          isValid: true,
          isComplete: true,
          message: 'RUT válido',
          showError: false
        };
      } else {
        return {
          isValid: false,
          isComplete: true,
          message: 'Dígito verificador incorrecto',
          showError: true
        };
      }
    },
    
    /**
     * Calcula el dígito verificador de un RUT chileno
     * @param {string} rutNumber - Número del RUT sin puntos ni guión
     * @returns {string} - Dígito verificador calculado
     */
    calculateDv(rutNumber) {
      let suma = 0;
      let multiplicador = 2;
      
      // Calcular suma ponderada
      for (let i = rutNumber.length - 1; i >= 0; i--) {
        suma += parseInt(rutNumber[i]) * multiplicador;
        multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
      }
      
      // Calcular dígito verificador
      const resto = suma % 11;
      const dv = 11 - resto;
      
      // Retornar dígito verificador
      if (dv === 11) return '0';
      if (dv === 10) return 'k';
      return dv.toString();
    },
    
    /**
     * Formatea un RUT para mostrar con puntos y guión
     * @param {string} rut - RUT sin formato
     * @returns {string} - RUT formateado
     */
    formatRut(rut) {
      if (!rut) return '';
      
      // Remover todos los caracteres no numéricos excepto 'k'
      let cleanRut = rut.toString().toLowerCase().replace(/[^0-9k]/g, '');
      
      // Si no hay suficientes caracteres, retornar tal como está
      if (cleanRut.length < 2) return rut;
      
      // Si tiene menos de 7 dígitos, no formatear completamente
      if (cleanRut.length < 7) {
        // Solo agregar puntos si hay suficientes dígitos
        if (cleanRut.length >= 4) {
          const rutNumber = cleanRut.slice(0, -1);
          const dv = cleanRut.slice(-1);
          
          let formattedNumber = '';
          for (let i = rutNumber.length - 1, j = 0; i >= 0; i--, j++) {
            if (j > 0 && j % 3 === 0) {
              formattedNumber = '.' + formattedNumber;
            }
            formattedNumber = rutNumber[i] + formattedNumber;
          }
          
          return formattedNumber + '-' + dv;
        }
        return rut;
      }
      
      // Separar número y dígito verificador
      const rutNumber = cleanRut.slice(0, -1);
      const dv = cleanRut.slice(-1);
      
      // Formatear número con puntos
      let formattedNumber = '';
      for (let i = rutNumber.length - 1, j = 0; i >= 0; i--, j++) {
        if (j > 0 && j % 3 === 0) {
          formattedNumber = '.' + formattedNumber;
        }
        formattedNumber = rutNumber[i] + formattedNumber;
      }
      
      // Retornar RUT formateado
      return formattedNumber + '-' + dv;
    },
    
    /**
     * Formatea un RUT mientras se escribe, agregando puntos y guión automáticamente
     * @param {string} rut - RUT sin formato
     * @returns {string} - RUT formateado
     */
    formatRutWhileTyping(rut) {
      if (!rut) return '';
      
      // Remover todos los caracteres no numéricos excepto 'k'
      let cleanRut = rut.toString().toLowerCase().replace(/[^0-9k]/g, '');
      
      // Si no hay caracteres, retornar vacío
      if (cleanRut.length === 0) return '';
      
      // Si solo hay un carácter, retornarlo tal como está
      if (cleanRut.length === 1) return cleanRut;
      
      // Si hay 2 caracteres, agregar guión después del primer carácter
      if (cleanRut.length === 2) {
        return cleanRut[0] + '-' + cleanRut[1];
      }
      
      // Si hay 3-5 caracteres, agregar puntos y guión
      if (cleanRut.length >= 3 && cleanRut.length <= 5) {
        const rutNumber = cleanRut.slice(0, -1);
        const dv = cleanRut.slice(-1);
        
        let formattedNumber = '';
        for (let i = rutNumber.length - 1, j = 0; i >= 0; i--, j++) {
          if (j > 0 && j % 3 === 0) {
            formattedNumber = '.' + formattedNumber;
          }
          formattedNumber = rutNumber[i] + formattedNumber;
        }
        
        return formattedNumber + '-' + dv;
      }
      
      // Si hay 6-8 caracteres, agregar puntos y guión
      if (cleanRut.length >= 6 && cleanRut.length <= 8) {
        const rutNumber = cleanRut.slice(0, -1);
        const dv = cleanRut.slice(-1);
        
        let formattedNumber = '';
        for (let i = rutNumber.length - 1, j = 0; i >= 0; i--, j++) {
          if (j > 0 && j % 3 === 0) {
            formattedNumber = '.' + formattedNumber;
          }
          formattedNumber = rutNumber[i] + formattedNumber;
        }
        
        return formattedNumber + '-' + dv;
      }
      
      // Si hay más de 8 caracteres, formatear completamente
      if (cleanRut.length > 8) {
        const rutNumber = cleanRut.slice(0, -1);
        const dv = cleanRut.slice(-1);
        
        let formattedNumber = '';
        for (let i = rutNumber.length - 1, j = 0; i >= 0; i--, j++) {
          if (j > 0 && j % 3 === 0) {
            formattedNumber = '.' + formattedNumber;
          }
          formattedNumber = rutNumber[i] + formattedNumber;
        }
        
        return formattedNumber + '-' + dv;
      }
      
      return cleanRut;
    },
    
    /**
     * Maneja el evento de input para validación en tiempo real
     * @param {Event} event - Evento de input
     * @param {string} fieldName - Nombre del campo
     */
    handleRutInput(event, fieldName = 'rut') {
      const value = event.target.value;
      
      // Formatear automáticamente mientras se escribe
      const formattedValue = this.formatRutWhileTyping(value);
      
      // Actualizar el modelo con el valor formateado
      if (this.formData && this.formData.rut !== undefined) {
        this.formData.rut = formattedValue;
      } else if (this.form && this.form.rut !== undefined) {
        this.form.rut = formattedValue;
      }
      
      // Validar y mostrar mensajes automáticamente
      const validation = this.validateRutChile(formattedValue);
      this.rutValidationState = validation;
      
      // Mostrar mensajes según el estado de validación
      if (validation.showError) {
        this.showRutValidationMessage(fieldName, validation);
      } else {
        this.clearRutValidationMessage(fieldName);
      }
      
      // Emitir evento de validación
      this.$emit('rut-validation-changed', {
        fieldName,
        value: formattedValue,
        isValid: this.rutValidationState.isValid,
        isComplete: this.rutValidationState.isComplete
      });
    },
    
    /**
     * Maneja el evento de blur para validación final
     * @param {Event} event - Evento de blur
     * @param {string} fieldName - Nombre del campo
     */
    handleRutBlur(event, fieldName = 'rut') {
      const value = event.target.value;
      
      // Validar el RUT cuando el campo pierde el foco
      const validation = this.validateRutChile(value);
      this.rutValidationState = validation;
      
      if (validation.showError) {
        this.showRutValidationMessage(fieldName, validation);
      } else {
        this.clearRutValidationMessage(fieldName);
      }
    },
    
    /**
     * Muestra u oculta el mensaje de validación del RUT
     * @param {string} fieldName - Nombre del campo
     * @param {Object} validation - Estado de validación
     */
    showRutValidationMessage(fieldName, validation) {
      const field = document.getElementById(fieldName);
      if (!field) return;
      
      const formGroup = field.closest('.form-group');
      if (!formGroup) return;
      
      // Remover TODOS los mensajes de error existentes (incluyendo los del sistema de validación)
      formGroup.querySelectorAll('.rut-error-message, label.error, .validation-message').forEach(el => el.remove());
      
      // Remover clases de estado
      formGroup.classList.remove('error', 'success');
      
      if (validation.showError && validation.message) {
        // Agregar clase de error
        formGroup.classList.add('error');
        
        // Crear mensaje de error
        const errorMessage = document.createElement('div');
        errorMessage.className = 'rut-error-message';
        errorMessage.textContent = validation.message;
        errorMessage.style.cssText = `
          color: #dc2626;
          font-size: 0.75rem;
          margin-top: 0.25rem;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          display: block;
          width: 100%;
        `;
        
        // Insertar mensaje al final del formGroup
        formGroup.appendChild(errorMessage);
        
        // Forzar actualización de la vista
        this.$forceUpdate();
      }
    },
    
    /**
     * Limpia el mensaje de validación del RUT
     * @param {string} fieldName - Nombre del campo
     */
    clearRutValidationMessage(fieldName) {
      const field = document.getElementById(fieldName);
      if (!field) return;
      
      const formGroup = field.closest('.form-group');
      if (!formGroup) return;
      
      // Remover TODOS los mensajes de error (incluyendo los del sistema de validación)
      formGroup.querySelectorAll('.rut-error-message, label.error, .validation-message').forEach(el => el.remove());
      
      // Remover clases de estado
      formGroup.classList.remove('error', 'success');
    },
    
    /**
     * Valida si un RUT es válido (método público)
     * @param {string} rut - RUT a validar
     * @returns {boolean} - true si es válido
     */
    isRutValid(rut) {
      const validation = this.validateRutChile(rut);
      return validation.isValid;
    },
    
    /**
     * Maneja la entrada de texto permitiendo escritura libre
     * @param {Event} event - Evento de input
     */
    handleRutKeypress(event) {
      const key = event.key;
      
      // Permitir números, letra k, puntos, guiones y teclas de control
      const allowedKeys = /[0-9kK.-]/;
      const controlKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      
      if (!allowedKeys.test(key) && !controlKeys.includes(key)) {
        event.preventDefault();
        return false;
      }
      
      // Permitir la escritura
      return true;
    },
    

  }
};
